import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Input from '@mui/joy/Input';
import Table from '@mui/joy/Table';
import Checkbox from '@mui/joy/Checkbox';
import CircularProgress from '@mui/joy/CircularProgress';
import Chip from '@mui/joy/Chip';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Textarea from '@mui/joy/Textarea';
import JoyTooltip from '@mui/joy/Tooltip';
import Switch from '@mui/joy/Switch';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Confirm from '../common/Confirm';
import {
  fetchRegistrations,
  fetchRegistrationDeployments,
  createRegistration,
  updateRegistration,
  fetchRegistration,
  activateRegistration,
  disableRegistration,
  deleteRegistration,
} from '../../api/registrations';

const SEARCH_DEBOUNCE_MS = 400;

/** Arrow-pointer tooltip matching the build panel, with a width cap so long text wraps. */
function Tooltip({ sx, children, ...props }) {
  return (
    <JoyTooltip
      arrow
      variant="solid"
      color="neutral"
      sx={{ maxWidth: 320, ...sx }}
      {...props}
    >
      {children}
    </JoyTooltip>
  );
}

/** Small info icon that reveals help text on hover/focus — the idiomatic MUI affordance for field guidance. */
function InfoTip({ title }) {
  return (
    <Tooltip placement="top-start" title={title}>
      <InfoOutlinedIcon
        tabIndex={0}
        role="img"
        sx={{ fontSize: '1rem', color: 'neutral.500', cursor: 'help', flexShrink: 0 }}
      />
    </Tooltip>
  );
}

/** FormLabel with a trailing info icon carrying the field's tooltip. */
function FieldLabel({ tip, children, sx }) {
  return (
    <FormLabel sx={{ gap: 0.5, ...sx }}>
      {children}
      {tip && <InfoTip title={tip} />}
    </FormLabel>
  );
}

function formatStatusColor(status) {
  if (status === 'active') return 'success';
  if (status === 'failed') return 'danger';
  return 'neutral';
}

function deploymentLabel(registration, deployment) {
  if (!registration.instanceId) return 'None';
  if (!deployment) return 'Linked';
  return deployment.agentName || 'Linked';
}

function isTerminalRegistrationState(row) {
  const status = String(row?.status || '').toLowerCase();
  const state = String(row?.state || '').toLowerCase();
  return (status === 'active' && state === 'registered') || (status === 'failed' && state === 'failed');
}

/** Rows that may be explicitly disabled (including failed/failed, which is otherwise terminal for refresh). */
function canDisableRegistration(row) {
  const status = String(row?.status || '').toLowerCase();
  const state = String(row?.state || '').toLowerCase();
  if (status === 'disabled') return false;
  if (status === 'active') return true;
  return status === 'failed' && state === 'failed';
}

// Telephony handlers a registration can route to. Mirrors the backend
// TELEPHONY_HANDLER_NAMES enum (llm-agent lib/handlers/index.js).
const REGISTRATION_HANDLER_OPTIONS = ['livekit', 'jambonz', 'pipecat'];

const DEFAULT_REGISTRATION = {
  name: '',
  registrar: '',
  username: '',
  password: '',
  handler: 'livekit',
  outbound: false,
  options: {
    transport: 'tls',
    register: true,
    register_proxy: '',
    realm: '',
    auth_username: '',
    extension_in_contact: false,
    ping: 30,
    bridged_transfer: false,
  },
};

function buildRegistrationExportObject(formData) {
  return {
    name: formData.name != null ? String(formData.name) : '',
    registrar: formData.registrar != null ? String(formData.registrar) : '',
    username: formData.username != null ? String(formData.username) : '',
    handler: formData.handler != null && String(formData.handler).trim() ? String(formData.handler) : DEFAULT_REGISTRATION.handler,
    outbound: !!formData.outbound,
    options: { ...(formData.options && typeof formData.options === 'object' ? formData.options : {}) },
  };
}

/**
 * Merge parsed JSON into form state: top-level main fields, `options`, and unknown top-level keys into options.
 * Does not read `password`.
 */
function applyImportedRegistrationJson(parsed, prevFormData) {
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error('Expected a JSON object');
  }
  const next = {
    ...prevFormData,
    options: { ...(prevFormData.options && typeof prevFormData.options === 'object' ? prevFormData.options : {}) },
  };
  const TOP = ['name', 'registrar', 'username', 'handler', 'outbound'];
  for (const key of TOP) {
    if (!Object.prototype.hasOwnProperty.call(parsed, key)) continue;
    if (key === 'outbound') {
      next.outbound = !!parsed[key];
    } else if (key === 'handler') {
      const h = parsed[key];
      next.handler =
        h != null && String(h).trim() ? String(h) : DEFAULT_REGISTRATION.handler;
    } else {
      const v = parsed[key];
      let s = v != null ? String(v) : '';
      if (key === 'registrar') s = s.trim();
      next[key] = s;
    }
  }
  const optionPatch = {};
  if (parsed.options != null && typeof parsed.options === 'object' && !Array.isArray(parsed.options)) {
    Object.assign(optionPatch, parsed.options);
  }
  const RESERVED = new Set([...TOP, 'password', 'options', 'id', 'type', 'status', 'state', 'error', 'instanceId']);
  for (const k of Object.keys(parsed)) {
    if (RESERVED.has(k)) continue;
    optionPatch[k] = parsed[k];
  }
  next.options = { ...next.options, ...optionPatch };
  return next;
}

function compressOptions(options) {
  const src = options || {};
  const out = { ...src };

  // transport: only include if not default 'tls'
  if (!src.transport || src.transport === 'tls') delete out.transport;

  // register: only include if explicitly false (default is true)
  if (src.register === true || src.register === undefined) delete out.register;

  // strings: only include non-empty
  if (!src.register_proxy) delete out.register_proxy;
  if (!src.realm) delete out.realm;
  if (!src.auth_username) delete out.auth_username;

  // extension_in_contact: only include if true (default false)
  if (!src.extension_in_contact) delete out.extension_in_contact;

  // ping: include if non-zero and different from default 30
  if (!(typeof src.ping === 'number' && src.ping !== 30)) delete out.ping;

  // bridged_transfer: only include if true (default false)
  if (!src.bridged_transfer) delete out.bridged_transfer;

  return out;
}

function normalizeHostPort(raw, transport) {
  const value = String(raw || '').trim();
  if (!value) return '';
  let host = value;
  let port = null;
  const idx = value.lastIndexOf(':');
  if (idx > 0 && idx < value.length - 1) {
    host = value.slice(0, idx).trim();
    port = value.slice(idx + 1).trim();
  }
  if (!port) {
    port = transport === 'tls' ? '5061' : '5060';
  }
  if (!host) {
    throw new Error('Host is required');
  }
  if (!/^[a-zA-Z0-9\-._:%]+$/.test(host)) {
    throw new Error('Host contains invalid characters');
  }
  if (!/^[0-9]+$/.test(port)) {
    throw new Error('Port must be numeric');
  }
  const portNum = Number(port);
  if (!Number.isFinite(portNum) || portNum <= 0 || portNum > 65535) {
    throw new Error('Port must be between 1 and 65535');
  }
  return `${host}:${portNum}`;
}

export default function RegistrationsPanel({ setError, setInform }) {
  const [registrations, setRegistrations] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [refreshingIds, setRefreshingIds] = useState(() => new Set());
  const [actionPendingIds, setActionPendingIds] = useState(() => new Set());
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);
  const [disableTarget, setDisableTarget] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create'); // 'create' | 'detail'
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_REGISTRATION);
  const [formTargetId, setFormTargetId] = useState(null);
  // When null, JSON textarea is derived from current options; when non-null, it is the raw user input (e.g. while invalid).
  const [advancedOptionsText, setAdvancedOptionsText] = useState(null);
  const [useCustomRegistrar, setUseCustomRegistrar] = useState(false);
  /** Registrar host:port values added from pasted JSON so the create-form Select can show them before any registration uses them. */
  const [extraFormRegistrars, setExtraFormRegistrars] = useState([]);

  const [filterSearch, setFilterSearch] = useState('');
  const [filterSearchDebounced, setFilterSearchDebounced] = useState('');
  const [registrarSearch, setRegistrarSearch] = useState('');
  const [selectedRegistrars, setSelectedRegistrars] = useState(() => new Set());

  useEffect(() => {
    const t = setTimeout(() => setFilterSearchDebounced(filterSearch), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [filterSearch]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [registrationItems, deploymentItems] = await Promise.all([
        fetchRegistrations(),
        fetchRegistrationDeployments()
      ]);
      setRegistrations(registrationItems);
      setDeployments(deploymentItems);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to load registrations';
      setLoadError(msg);
      setError?.(msg);
    } finally {
      setLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateForm = useCallback(() => {
    setFormMode('create');
    setFormTargetId(null);
    setFormData(DEFAULT_REGISTRATION);
    setFormError(null);
    setAdvancedOptionsText(null);
    setUseCustomRegistrar(false);
    setExtraFormRegistrars([]);
    setFormOpen(true);
  }, []);

  const openDetailForm = useCallback(async (row) => {
    setFormMode('detail');
    setFormTargetId(row.id);
    setFormError(null);
    try {
      const full = await fetchRegistration(row.id);
      const opts = full.options || {};
      setFormData({
        name: full.name || '',
        registrar: full.registrar || '',
        username: full.username || '',
        password: '',
        handler: full.handler || 'livekit',
        outbound: !!full.outbound,
        options: {
          transport: opts.transport || 'tls',
          register: opts.register !== false,
          register_proxy: opts.register_proxy || '',
          realm: opts.realm || '',
          auth_username: opts.auth_username || '',
          extension_in_contact: !!opts.extension_in_contact,
          ping: typeof opts.ping === 'number' ? opts.ping : 30,
          bridged_transfer: !!opts.bridged_transfer,
        },
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to load registration details';
      setFormError(msg);
      setError?.(msg);
      // Fallback to basic row data without options if needed
      setFormData((prev) => ({
        ...prev,
        name: row.name || '',
        registrar: row.registrar || '',
        username: row.username || '',
        handler: row.handler || 'livekit',
        outbound: !!row.outbound,
      }));
    }
    // Start with derived JSON from options; user edits will override as needed.
    setAdvancedOptionsText(null);
    setUseCustomRegistrar(true);
    setExtraFormRegistrars([]);
    setFormOpen(true);
  }, [setError]);

  const closeForm = useCallback(() => {
    setFormOpen(false);
    setFormError(null);
    setFormTargetId(null);
  }, []);

  const updateFormField = useCallback((path, value) => {
    setFormData((prev) => {
      const next = { ...prev };
      if (path.startsWith('options.')) {
        const key = path.slice('options.'.length);
        next.options = { ...(next.options || {}), [key]: value };
      } else {
        next[path] = value;
      }
      return next;
    });
  }, []);

  const handleFormSubmit = useCallback(async () => {
    setFormSaving(true);
    setFormError(null);
    try {
      const transport = formData.options?.transport || 'tls';
      let registrar;
      let registerProxy;
      try {
        registrar = normalizeHostPort(formData.registrar, transport);
      } catch (e) {
        throw new Error(`Registrar: ${e.message}`);
      }
      if (formData.options?.register_proxy) {
        try {
          registerProxy = normalizeHostPort(formData.options.register_proxy, transport);
        } catch (e) {
          throw new Error(`Register proxy: ${e.message}`);
        }
      }
      const optionsWithNormalized = {
        ...(formData.options || {}),
        register_proxy: registerProxy || '',
      };
      // For updates, treat blank password as "keep existing" by omitting it
      const passwordForPayload =
        formMode === 'detail' && (formData.password == null || String(formData.password).trim() === '')
          ? undefined
          : formData.password;
      const payload = {
        ...formData,
        password: passwordForPayload,
        registrar,
        options: compressOptions(optionsWithNormalized),
      };
      if (formMode === 'create') {
        const created = await createRegistration(payload);
        const createdId = created?.id;
        if (createdId) {
          await activateRegistration(createdId);
        }
      } else if (formMode === 'detail' && formTargetId) {
        await updateRegistration(formTargetId, payload);
      }
      await loadData();
      setFormOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to save registration';
      setFormError(msg);
      setError?.(msg);
    } finally {
      setFormSaving(false);
    }
  }, [formMode, formTargetId, formData, loadData, setError]);

  const deploymentByListenerId = useMemo(() => {
    const map = new Map();
    deployments.forEach((item) => {
      if (item.listenerId) map.set(item.listenerId, item);
    });
    return map;
  }, [deployments]);

  const registrars = useMemo(() => {
    const unique = new Set();
    registrations.forEach((item) => {
      if (item.registrar && String(item.registrar).trim()) unique.add(item.registrar);
    });
    return [...unique].sort((a, b) => a.localeCompare(b));
  }, [registrations]);

  const formRegistrarOptions = useMemo(() => {
    const unique = new Set(registrars);
    extraFormRegistrars.forEach((r) => {
      if (r && String(r).trim()) unique.add(String(r).trim());
    });
    return [...unique].sort((a, b) => a.localeCompare(b));
  }, [registrars, extraFormRegistrars]);

  const visibleRegistrars = useMemo(() => {
    const q = String(registrarSearch).trim().toLowerCase();
    if (!q) return registrars;
    return registrars.filter((registrar) => registrar.toLowerCase().includes(q));
  }, [registrars, registrarSearch]);

  const filteredRegistrations = useMemo(() => {
    const q = String(filterSearchDebounced).trim().toLowerCase();
    return registrations.filter((item) => {
      const matchesSearch = !q
        || (item.name && item.name.toLowerCase().includes(q))
        || (item.username && item.username.toLowerCase().includes(q));
      const matchesRegistrar = selectedRegistrars.size === 0 || selectedRegistrars.has(item.registrar);
      return matchesSearch && matchesRegistrar;
    });
  }, [registrations, filterSearchDebounced, selectedRegistrars]);

  const detailRegistrationRow = useMemo(
    () => (formTargetId ? registrations.find((r) => r.id === formTargetId) : null),
    [formTargetId, registrations],
  );
  const detailRegistrationIsDisabled =
    String(detailRegistrationRow?.status || '').toLowerCase() === 'disabled';

  const toggleRegistrarSelection = useCallback((registrar) => {
    setSelectedRegistrars((prev) => {
      const next = new Set(prev);
      if (next.has(registrar)) next.delete(registrar);
      else next.add(registrar);
      return next;
    });
  }, []);

  const clearRegistrarSelection = useCallback(() => {
    setSelectedRegistrars(new Set());
  }, []);

  const copyRegistrationIdToClipboard = useCallback(async () => {
    const id = formTargetId;
    if (!id) return;
    try {
      await navigator.clipboard.writeText(String(id));
      setInform?.({ success: `Copied to clipboard: ${String(id)}` });
    } catch {
      setError?.('Could not copy to clipboard.');
    }
  }, [formTargetId, setError, setInform]);

  const copyRegistrationConfig = useCallback(async () => {
    try {
      const payload = buildRegistrationExportObject(formData);
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setInform?.({ success: 'Registration config copied to clipboard.' });
    } catch {
      setError?.('Could not copy to clipboard.');
    }
  }, [formData, setError, setInform]);

  const handleAdvancedOptionsChange = useCallback(
    (e) => {
      const value = e.target.value;
      try {
        const parsed = value.trim() === '' ? {} : JSON.parse(value);
        setFormData((prev) => applyImportedRegistrationJson(parsed, prev));
        setAdvancedOptionsText(null);
        setFormError(null);
        if (
          typeof parsed === 'object' &&
          parsed !== null &&
          !Array.isArray(parsed) &&
          Object.prototype.hasOwnProperty.call(parsed, 'registrar')
        ) {
          const imported = parsed.registrar != null ? String(parsed.registrar).trim() : '';
          if (imported) {
            if (!registrars.includes(imported)) {
              setExtraFormRegistrars((prev) => (prev.includes(imported) ? prev : [...prev, imported]));
            }
            setUseCustomRegistrar(false);
          }
        }
      } catch {
        setAdvancedOptionsText(value);
        setFormError('Invalid JSON in advanced options');
      }
    },
    [registrars],
  );

  const refreshRowStatus = useCallback(async (row) => {
    const id = row?.id;
    if (!id) return;
    setRefreshingIds((prev) => new Set(prev).add(id));
    try {
      const latest = await fetchRegistration(id);
      setRegistrations((prev) => prev.map((r) => (r.id === id ? { ...r, ...latest } : r)));
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to refresh registration status';
      setError?.(msg);
    } finally {
      setRefreshingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [setError]);

  const setPendingId = useCallback((id, pending) => {
    if (!id) return;
    setActionPendingIds((prev) => {
      const next = new Set(prev);
      if (pending) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const applyRegistrationPatch = useCallback((id, patch) => {
    if (!id) return;
    setRegistrations((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const handleActivateRow = useCallback(async (row) => {
    const id = row?.id;
    if (!id) return;
    setPendingId(id, true);
    try {
      const result = await activateRegistration(id);
      applyRegistrationPatch(id, { ...(result || {}), status: result?.status || 'active', state: result?.state || 'initial' });
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to activate registration';
      setError?.(msg);
    } finally {
      setPendingId(id, false);
    }
  }, [applyRegistrationPatch, setError, setPendingId]);

  const requestDisableRow = useCallback((row) => {
    setDisableTarget(row || null);
    setDisableConfirmOpen(true);
  }, []);

  const handleDisableConfirm = useCallback(() => {
    const id = disableTarget?.id;
    if (!id) return;
    (async () => {
      setPendingId(id, true);
      try {
        const result = await disableRegistration(id);
        applyRegistrationPatch(id, { ...(result || {}), status: result?.status || 'disabled', state: result?.state || 'initial' });
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to disable registration';
        setError?.(msg);
      } finally {
        setPendingId(id, false);
      }
    })();
  }, [applyRegistrationPatch, disableTarget, setError, setPendingId]);

  const requestDeleteRow = useCallback((row) => {
    if (String(row?.status || '').toLowerCase() !== 'disabled') return;
    setDeleteTarget(row || null);
    setDeleteConfirmOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    const id = deleteTarget?.id;
    if (!id || String(deleteTarget?.status || '').toLowerCase() !== 'disabled') return;
    (async () => {
      setPendingId(id, true);
      try {
        await deleteRegistration(id);
        if (formOpen && formMode === 'detail' && formTargetId === id) {
          closeForm();
        }
        await loadData();
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to delete registration';
        setError?.(msg);
      } finally {
        setPendingId(id, false);
      }
    })();
  }, [closeForm, deleteTarget, formMode, formOpen, formTargetId, loadData, setError, setPendingId]);

  return (
    <Box sx={{ py: 2 }}>
      <Typography level="h4" sx={{ display: { xs: 'none', sm: 'block' } }}>
        SIP Registrations
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateRows: '320px minmax(0, 1fr)',
          gridTemplateColumns: '1fr',
          gap: 2,
          mt: 2,
          height: 'calc(100vh - 220px)',
          minHeight: 360,
        }}
      >
        <Box
          sx={{
            minHeight: 0,
            maxHeight: '320px',
            overflow: 'hidden',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
            gap: 2,
          }}
        >
          <Sheet
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 'sm',
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                Registration search
              </Typography>
              <Input
                size="sm"
                placeholder="Filter by name or username"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
              />
            </Box>
            <Typography level="body2" color="neutral">
              Select registrars in the right panel to narrow the list.
            </Typography>
            <Button size="sm" variant="solid" onClick={openCreateForm}>
              Add registration
            </Button>
          </Sheet>

          <Sheet
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 'sm',
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography level="title-sm" sx={{ flexGrow: 1 }}>
                Registrars
              </Typography>
              {selectedRegistrars.size > 0 && (
                <Button size="sm" variant="plain" color="neutral" onClick={clearRegistrarSelection}>
                  Unselect all
                </Button>
              )}
              <Input
                size="sm"
                placeholder="Search registrars"
                value={registrarSearch}
                onChange={(e) => setRegistrarSearch(e.target.value)}
                sx={{ maxWidth: 240 }}
              />
            </Box>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size="sm" />
              </Box>
            )}
            {!loading && (
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <Table size="sm" stickyHeader sx={{ '--TableCell-paddingX': '0.75rem' }}>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }} aria-label="Select" />
                      <th>Registrar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRegistrars.length === 0 && (
                      <tr>
                        <td colSpan={2}>
                          <Typography level="body2" color="neutral">No registrars</Typography>
                        </td>
                      </tr>
                    )}
                    {visibleRegistrars.map((registrar) => (
                      <tr
                        key={registrar}
                        onClick={(e) => {
                          if (e.target.closest('input')) return;
                          toggleRegistrarSelection(registrar);
                        }}
                        style={{
                          cursor: 'pointer',
                          background: selectedRegistrars.has(registrar) ? 'var(--joy-palette-primary-softBg)' : undefined
                        }}
                      >
                        <td>
                          <Checkbox
                            size="sm"
                            checked={selectedRegistrars.has(registrar)}
                            onChange={() => toggleRegistrarSelection(registrar)}
                            aria-label={`Select ${registrar}`}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td>{registrar}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Box>
            )}
          </Sheet>
        </Box>

        <Sheet
          variant="outlined"
          sx={{
            borderRadius: 'sm',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, pb: 0, flexShrink: 0, flexWrap: 'wrap' }}>
            <Typography level="title-sm" sx={{ flexGrow: 1 }}>
              Registrations
            </Typography>
            <Typography level="body2" color="neutral">
              {filteredRegistrations.length} shown
            </Typography>
          </Box>
          {loadError && (
            <Typography level="body2" color="danger" sx={{ px: 2, pt: 1 }}>
              {loadError}
            </Typography>
          )}
          {!loading && filteredRegistrations.length === 0 && (
            <Typography level="body2" color="neutral" sx={{ p: 2 }}>
              No registrations match the current filters.
            </Typography>
          )}
          {filteredRegistrations.length > 0 && (
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Table stickyHeader size="sm" sx={{ '--TableCell-paddingX': '0.75rem' }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Username</th>
                    <th>Registrar</th>
                    <th style={{ width: '8%', whiteSpace: 'nowrap' }}>Handler</th>
                    <th style={{ width: '8%', whiteSpace: 'nowrap' }}>Outbound</th>
                    <th>Status</th>
                    <th>Agent</th>
                    <th aria-label="Actions" style={{ width: 56 }} />
                  </tr>
                </thead>
                <tbody>
                  {filteredRegistrations.map((row) => {
                    const deployment = row.instanceId ? deploymentByListenerId.get(row.instanceId) : null;
                    const terminal = isTerminalRegistrationState(row);
                    const canRefresh = !terminal;
                    const isRefreshing = row?.id ? refreshingIds.has(row.id) : false;
                    const isPending = row?.id ? actionPendingIds.has(row.id) : false;
                    return (
                      <tr
                        key={row.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => openDetailForm(row)}
                      >
                        <td>{row.name || '–'}</td>
                        <td>{row.username || '–'}</td>
                        <td>{row.registrar || '–'}</td>
                        <td style={{ width: '8%', whiteSpace: 'nowrap' }}>{row.handler || '–'}</td>
                        <td style={{ width: '8%', whiteSpace: 'nowrap' }}>{row.outbound ? 'Yes' : 'No'}</td>
                        <td>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip size="sm" variant="soft" color={formatStatusColor(row.status)}>
                              {row.status || 'unknown'}
                            </Chip>
                            {row.state && (
                              <Typography level="body3" color="neutral">
                                {row.state}
                              </Typography>
                            )}
                          </Box>
                        </td>
                        <td>{deploymentLabel(row, deployment)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            <Tooltip
                              title={canRefresh ? 'Refresh live status' : 'Status is in a terminal state'}
                              placement="left"
                            >
                              <span>
                                <IconButton
                                  size="sm"
                                  variant="plain"
                                  color="neutral"
                                  disabled={!canRefresh || isRefreshing || isPending}
                                  aria-label="Refresh status"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    refreshRowStatus(row);
                                  }}
                                >
                                  {isRefreshing ? <CircularProgress size="sm" /> : <RefreshIcon />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Box>
          )}
        </Sheet>
      </Box>

      <Confirm
        open={disableConfirmOpen}
        setOpen={(open) => {
          setDisableConfirmOpen(open);
          if (!open) setDisableTarget(null);
        }}
        onConfirm={handleDisableConfirm}
        title="Disable registration?"
      >
        <Typography level="body-sm">
          This will disable the SIP registration and stop any ongoing retry attempts.
        </Typography>
        {String(disableTarget?.status || '').toLowerCase() === 'failed' &&
          String(disableTarget?.state || '').toLowerCase() === 'failed' && (
          <Typography level="body-sm" sx={{ mt: 1 }}>
            For a failed registration, disabling clears the error state and stops retries until you re-activate or fix
            credentials and settings.
          </Typography>
        )}
        {disableTarget?.name && (
          <Typography level="body-xs" textColor="neutral.600" sx={{ mt: 1 }}>
            {disableTarget.name}
          </Typography>
        )}
        {disableTarget?.id && (
          <Typography
            level="body-xs"
            textColor="neutral.500"
            sx={{ mt: 1, fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}
          >
            {disableTarget.id}
          </Typography>
        )}
      </Confirm>

      <Confirm
        open={deleteConfirmOpen}
        setOpen={(open) => {
          setDeleteConfirmOpen(open);
          if (!open) setDeleteTarget(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete registration?"
      >
        <Typography level="body-sm">This permanently removes the SIP registration endpoint.</Typography>
        {deleteTarget?.name && (
          <Typography level="body-xs" textColor="neutral.600" sx={{ mt: 1 }}>
            {deleteTarget.name}
          </Typography>
        )}
        {deleteTarget?.id && (
          <Typography
            level="body-xs"
            textColor="neutral.500"
            sx={{ mt: 1, fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}
          >
            {deleteTarget.id}
          </Typography>
        )}
      </Confirm>

      <Modal open={formOpen} onClose={closeForm}>
        <ModalDialog
          sx={{
            maxWidth: 560,
            width: '100%',
            // Anchor near the top instead of vertically centering, so the
            // dialog doesn't shift as inner content (e.g. the JSON box) grows.
            top: '5rem',
            transform: 'translateX(-50%)',
            maxHeight: 'calc(100dvh - 7rem)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <ModalClose onClick={closeForm} />
          <Typography level="h6" sx={{ mb: 1 }}>
            {formMode === 'create' ? 'Add registration' : 'Registration details'}
          </Typography>
          {formMode === 'create' && (
            <Typography level="body2" color="neutral" sx={{ mb: 2 }}>
              Configure how this SIP registration connects to your provider, including credentials, transport, proxies,
              and optional keepalive behaviour.
            </Typography>
          )}
          {formMode === 'detail' && formTargetId && (
            <Box sx={{ mb: 2 }}>
              <Typography level="body-xs" textColor="neutral.600" sx={{ mb: 0.5 }}>
                Registration ID
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  level="body-sm"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontFamily: 'ui-monospace, monospace',
                    wordBreak: 'break-all',
                  }}
                >
                  {formTargetId}
                </Typography>
                <Tooltip title="Copy registration ID" placement="top">
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    aria-label="Copy registration ID"
                    onClick={() => copyRegistrationIdToClipboard()}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
                <Chip
                  size="sm"
                  variant="soft"
                  color={detailRegistrationIsDisabled ? 'neutral' : 'success'}
                  sx={{ flexShrink: 0 }}
                >
                  {detailRegistrationIsDisabled ? 'Disabled' : 'Enabled'}
                </Chip>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              pr: 1,
            }}
          >
            <FormControl>
              <FormLabel>Name (optional)</FormLabel>
              <Input
                size="sm"
                value={formData.name}
                onChange={(e) => updateFormField('name', e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FieldLabel tip="Which internal trunk the b2bua bridges calls from this registration to. 'livekit' and 'pipecat' route to their respective agent runtimes; 'jambonz' uses the legacy STT→LLM→TTS pipeline.">
                Handler
              </FieldLabel>
              <Select
                size="sm"
                value={formData.handler || 'livekit'}
                onChange={(_, v) => updateFormField('handler', v || 'livekit')}
              >
                {REGISTRATION_HANDLER_OPTIONS.map((h) => (
                  <Option key={h} value={h}>
                    {h}
                  </Option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FieldLabel tip="Registrar host:port for your SIP provider (e.g. signalling address for inbound/outbound calls).">
                Registrar (host:port)
              </FieldLabel>
              {formMode === 'create' ? (
                <>
                  <Select
                    size="sm"
                    placeholder="Choose existing registrar or Custom…"
                    value={useCustomRegistrar ? '__custom' : (formData.registrar || null)}
                    onChange={(_, v) => {
                      if (v === '__custom') {
                        setUseCustomRegistrar(true);
                        updateFormField('registrar', '');
                      } else {
                        setUseCustomRegistrar(false);
                        updateFormField('registrar', v || '');
                      }
                    }}
                    sx={{ mb: 1 }}
                  >
                    {formRegistrarOptions.map((r) => (
                      <Option key={r} value={r}>
                        {r}
                      </Option>
                    ))}
                      <Option value="__custom">New…</Option>
                  </Select>
                  {useCustomRegistrar && (
                    <Input
                      size="sm"
                      placeholder="provider.example.com[:port]"
                      value={formData.registrar}
                      onChange={(e) => updateFormField('registrar', e.target.value)}
                    />
                  )}
                </>
              ) : (
                <Input
                  size="sm"
                  placeholder="provider.example.com:5060"
                  value={formData.registrar}
                  onChange={(e) => updateFormField('registrar', e.target.value)}
                />
              )}
            </FormControl>
            <FormControl>
              <FormLabel>Username</FormLabel>
              <Input
                size="sm"
                value={formData.username}
                onChange={(e) => updateFormField('username', e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Password</FormLabel>
              <Input
                size="sm"
                type="password"
                value={formData.password}
                onChange={(e) => updateFormField('password', e.target.value)}
                placeholder={formMode === 'detail' ? 'Leave blank to keep existing password' : ''}
              />
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Switch
                  checked={!!formData.outbound}
                  onChange={(e) => updateFormField('outbound', e.target.checked)}
                  size="sm"
                />
                <Typography level="body2">Allow outbound calling</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Switch
                  checked={!!formData.options.bridged_transfer}
                  onChange={(e) => updateFormField('options.bridged_transfer', e.target.checked)}
                  size="sm"
                />
                <Typography level="body2">Bridged transfer</Typography>
                <InfoTip title="By default, transfers on registration endpoints are completed with a SIP REFER (the provider routes the caller on directly). Enable this to instead keep the media bridged through Aplisay for the final transfer hop — use when the provider doesn't handle REFER / REFER-with-Replaces reliably." />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 160 }}>
                <FieldLabel tip="Network transport for REGISTER and related SIP traffic (typically udp, tcp, or tls).">
                  Transport
                </FieldLabel>
                <Input
                  size="sm"
                  value={formData.options.transport}
                  onChange={(e) => updateFormField('options.transport', e.target.value)}
                  placeholder="tls"
                />
              </FormControl>
              <FormControl sx={{ minWidth: 160 }}>
                <FieldLabel tip="Optional outbound proxy host:port used for signalling instead of the registrar.">
                  Register proxy (optional)
                </FieldLabel>
                <Input
                  size="sm"
                  value={formData.options.register_proxy}
                  onChange={(e) => updateFormField('options.register_proxy', e.target.value)}
                  placeholder="proxy.example.com:5060"
                />
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 160 }}>
                <FieldLabel tip="Authentication realm or account domain expected by your provider (often the SIP domain).">
                  Realm (optional)
                </FieldLabel>
                <Input
                  size="sm"
                  value={formData.options.realm}
                  onChange={(e) => updateFormField('options.realm', e.target.value)}
                  placeholder="provider.example.com"
                />
              </FormControl>
              <FormControl sx={{ minWidth: 160 }}>
                <FieldLabel tip="Alternative auth username if different from the main username.">
                  Auth username (optional)
                </FieldLabel>
                <Input
                  size="sm"
                  value={formData.options.auth_username}
                  onChange={(e) => updateFormField('options.auth_username', e.target.value)}
                />
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Switch
                  checked={!!formData.options.register}
                  onChange={(e) => updateFormField('options.register', e.target.checked)}
                  size="sm"
                />
                <Typography level="body2">Send REGISTER to provider</Typography>
                <InfoTip title="Whether this endpoint should actively REGISTER with the provider." />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Switch
                  checked={!!formData.options.extension_in_contact}
                  onChange={(e) => updateFormField('options.extension_in_contact', e.target.checked)}
                  size="sm"
                />
                <Typography level="body2">Extension in Contact</Typography>
                <InfoTip title="Include the extension in the SIP Contact header for this registration." />
              </Box>
            </Box>
            <FormControl>
              <FieldLabel tip="Interval in seconds between keepalive pings (0 disables pings).">
                Ping interval (seconds)
              </FieldLabel>
              <Input
                size="sm"
                type="number"
                value={formData.options.ping === '' ? '' : formData.options.ping}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    updateFormField('options.ping', '');
                  } else {
                    const num = Number(raw);
                    updateFormField('options.ping', Number.isFinite(num) ? num : 0);
                  }
                }}
                slotProps={{ input: { min: 0 } }}
              />
            </FormControl>
            <FormControl>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 1,
                  flexWrap: 'wrap',
                  mb: 0.5,
                }}
              >
                <FormLabel sx={{ mb: 0 }}>Advanced options (JSON, optional)</FormLabel>
                <Button
                  type="button"
                  size="sm"
                  variant="plain"
                  color="neutral"
                  startDecorator={<ContentCopyIcon />}
                  onClick={() => copyRegistrationConfig()}
                  sx={{ fontWeight: 'normal' }}
                >
                  Copy config
                </Button>
              </Box>
              <Textarea
                minRows={6}
                value={
                  advancedOptionsText != null
                    ? advancedOptionsText
                    : JSON.stringify(compressOptions(formData.options || {}), null, 2)
                }
                onChange={handleAdvancedOptionsChange}
              />
            </FormControl>
            {formError && (
              <Typography level="body2" color="danger">
                {formError}
              </Typography>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                mt: 1,
              }}
            >
              <Button variant="outlined" color="neutral" onClick={closeForm}>
                Cancel
              </Button>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
                {formMode === 'detail' &&
                  detailRegistrationRow &&
                  canDisableRegistration(detailRegistrationRow) &&
                  !detailRegistrationIsDisabled && (
                    <Button
                      variant="outlined"
                      color="danger"
                      disabled={formSaving || (formTargetId && actionPendingIds.has(formTargetId))}
                      onClick={() => requestDisableRow(detailRegistrationRow)}
                    >
                      Disable
                    </Button>
                  )}
                {formMode === 'detail' && detailRegistrationIsDisabled && (
                  <>
                    <Button
                      variant="outlined"
                      color="success"
                      disabled={formSaving || (formTargetId && actionPendingIds.has(formTargetId))}
                      onClick={() =>
                        handleActivateRow(
                          detailRegistrationRow || {
                            id: formTargetId,
                            status: 'disabled',
                          },
                        )
                      }
                    >
                      Enable
                    </Button>
                    <Button
                      variant="outlined"
                      color="danger"
                      disabled={formSaving || (formTargetId && actionPendingIds.has(formTargetId))}
                      onClick={() =>
                        requestDeleteRow(
                          detailRegistrationRow || {
                            id: formTargetId,
                            name: formData.name,
                            status: 'disabled',
                          },
                        )
                      }
                    >
                      Delete
                    </Button>
                  </>
                )}
                <Button variant="solid" loading={formSaving} onClick={handleFormSubmit}>
                  {formMode === 'create' ? 'Create' : 'Save'}
                </Button>
              </Box>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
